import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { Device } from '@capacitor/device'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { post, remove } from 'src/axios/post'
import { makeGuid } from 'src/helpers/globals'

/**
 * Biometric login keeps an API-issued device key in secure storage instead of the
 * user's password. The key is exchanged for a session via v1/user/signIn/biometric.
 *
 * A device can hold a key per account, so keys are stored as one map keyed by email:
 * { [email]: { token, guid, usedAt } }. Every lookup takes the email of the account in question.
 */
const ACCOUNTS_KEY = 'biometricAccounts'
// When the user last chose "Maybe later" on the post-login offer, per email
const DECLINED_KEY = 'biometricOfferDeclines'
const OFFER_AGAIN_AFTER_DAYS = 30
// Keys written by the old "Remember Me" flow (which stored the password itself)
// and by the first single-account version of biometric login
const LEGACY_KEYS = [
  'login_details', 'rememberMe', 'biometricsEnabled', 'biometricOfferDeclined',
  'biometricToken', 'biometricGuid', 'biometricEmail', 'biometricOfferDeclinedAt'
]

export function accountKey (email) {
  return (email || '').trim().toLowerCase()
}

async function readMap (key) {
  try {
    return JSON.parse((await SecureStoragePlugin.get({ key })).value) || {}
  }
  catch {
    return {}
  }
}

async function writeMap (key, value) {
  await SecureStoragePlugin.set({ key, value: JSON.stringify(value) })
}

async function forget (key) {
  try {
    await SecureStoragePlugin.remove({ key })
  }
  catch {
    // Missing key
  }
}

/** All device keys on this device, keyed by normalised email. */
export function getBiometricAccounts () {
  return readMap(ACCOUNTS_KEY)
}

/** The email of the account that most recently enabled or used biometric login, or null. */
export function mostRecentBiometricEmail (accounts) {
  const [email] = Object.entries(accounts).sort(([, a], [, b]) => b.usedAt - a.usedAt)[0] || []
  return email || null
}

export async function getBiometricKey (email) {
  return (await getBiometricAccounts())[accountKey(email)] || null
}

export async function hasBiometricKey (email) {
  return !!(await getBiometricKey(email))
}

export async function forgetBiometricKey (email) {
  const accounts = await getBiometricAccounts()
  delete accounts[accountKey(email)]
  await writeMap(ACCOUNTS_KEY, accounts)
}

/** Mark the account's key as just used, so the login page prefills this account next time. */
export async function rememberBiometricLogin (email) {
  const accounts = await getBiometricAccounts()
  const key = accounts[accountKey(email)]
  if (key) {
    key.usedAt = Date.now()
    await writeMap(ACCOUNTS_KEY, accounts)
  }
}

export async function forgetLegacyCredentials () {
  await Promise.all(LEGACY_KEYS.map(forget))
}

/** Whether to show the post-login offer: not declined for this account, or declined long enough ago to ask again. */
export async function shouldOfferBiometrics (email) {
  const declinedAt = Number((await readMap(DECLINED_KEY))[accountKey(email)])
  if (!declinedAt) {
    return true
  }
  return Date.now() - declinedAt > OFFER_AGAIN_AFTER_DAYS * 24 * 60 * 60 * 1000
}

export async function rememberOfferDeclined (email) {
  const declined = await readMap(DECLINED_KEY)
  declined[accountKey(email)] = Date.now()
  await writeMap(DECLINED_KEY, declined)
}

async function forgetOfferDeclined (email) {
  const declined = await readMap(DECLINED_KEY)
  delete declined[accountKey(email)]
  await writeMap(DECLINED_KEY, declined)
}

/**
 * Prompt for biometrics, then swap the password for a device key.
 * Throws the plugin's BiometryError if the prompt fails; returns the API envelope otherwise.
 */
export async function enableBiometricLogin (email, password, reason) {
  await BiometricAuth.authenticate({ reason, allowDeviceCredential: true })

  const guid = makeGuid()
  const info = await Device.getInfo().catch(() => ({}))
  const response = await post('v1/user/biometric', {
    password,
    guid,
    device_name: info.name || info.model || null
  })

  if (response.status === 'success') {
    const accounts = await getBiometricAccounts()
    accounts[accountKey(email)] = { token: response.payload.token, guid, usedAt: Date.now() }
    await writeMap(ACCOUNTS_KEY, accounts)
  }

  return response
}

/** Revoke this device's key for the account on the API, then forget it locally. */
export async function disableBiometricLogin (email) {
  const key = await getBiometricKey(email)
  try {
    if (key) {
      const { status } = await remove('v1/user/biometric', { guid: key.guid })
      if (status !== 'success') {
        throw new Error('Could not revoke the device key')
      }
    }
  }
  finally {
    // The user turned it off: drop the key here even if the API could not be reached,
    // so the login screen stops offering a fingerprint the server may no longer accept.
    // It is also a deliberate choice, so the next password login may offer it again.
    await Promise.all([forgetBiometricKey(email), forgetOfferDeclined(email)])
  }
}
